<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import V4Button from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import ReportHeader from '../../../reports/components/ReportHeader.vue';
import DashboardWidget from './components/DashboardWidget.vue';
import PeriodRangePicker from './components/PeriodRangePicker.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const dashboard = useMapGetter('wgptCustomDashboards/getActiveDashboard');
const uiFlags = useMapGetter('wgptCustomDashboards/getUIFlags');

const dashboardId = computed(() => route.params.dashboardId);
const widgets = computed(() => dashboard.value?.widgets || []);

// Dashboard-wide period filter, applied to every widget at runtime. Seed the
// default so widgets have a range before the picker emits any change.
// Seeded by PeriodRangePicker's mount-time emit — seeding a second range here
// would differ by milliseconds and make every widget fetch twice.
const dashboardPeriod = ref(null);

function widgetGridClass(widget) {
  return widget.width === 'full' ? 'col-span-1 md:col-span-2' : 'col-span-1';
}

async function fetchDashboard() {
  try {
    await store.dispatch('wgptCustomDashboards/show', dashboardId.value);
  } catch {
    useAlert(t('WGPT_CUSTOM_DASHBOARDS.VIEWER.ERROR'));
  }
}

function navigateToEdit() {
  router.push(
    accountScopedRoute('wgpt_settings_custom_dashboards_edit', {
      dashboardId: dashboardId.value,
    })
  );
}

onMounted(fetchDashboard);
watch(dashboardId, fetchDashboard);
</script>

<template>
  <div>
    <ReportHeader
      :header-title="dashboard.name || t('WGPT_CUSTOM_DASHBOARDS.HEADER')"
      has-back-button
    >
      <V4Button
        icon="i-lucide-pencil"
        variant="ghost"
        size="sm"
        @click="navigateToEdit"
      />
    </ReportHeader>

    <p v-if="dashboard.description" class="text-sm text-n-slate-11 mb-4">
      {{ dashboard.description }}
    </p>

    <div v-if="widgets.length" class="flex flex-wrap items-center gap-3 mb-6">
      <PeriodRangePicker @change="dashboardPeriod = $event" />
    </div>

    <div
      v-if="uiFlags.isFetchingItem"
      class="flex items-center justify-center py-16"
    >
      <Spinner />
    </div>

    <div
      v-else-if="!widgets.length"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('WGPT_CUSTOM_DASHBOARDS.VIEWER.NO_WIDGETS') }}
      </p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="widget in widgets"
        :key="widget.id"
        :class="widgetGridClass(widget)"
      >
        <DashboardWidget :widget="widget" :period="dashboardPeriod" drillable />
      </div>
    </div>
  </div>
</template>
